import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Skeleton, useToast } from '../../../../ds/components';
import { clsx } from '../../../../ds/clsx';
import { ApiError, api, errorMessage } from '../../../../lib/api';
import { WizardButton, WizardField, WizardFooter, WizardIntro } from '../../ui';
import { organizationKeys, useDomain } from '../api';
import {
  DomainVerifiedCard,
  DomainWaitingCard,
  RecordsTable,
  domainCardStyles,
  recordsText,
} from '../DomainStatusCards';
import { ItMailDialog } from '../ItMailDialog';
import { LoadError } from '../LoadError';
import type { Domain, DomainAvailability } from '../types';
import { useWizardNav } from '../wizardContext';
import { stepEyebrow } from '../wizardSteps';
import styles from './steps.module.css';

type View = 'setup' | 'status';

function initialView(domain: Domain): View {
  if (domain.state === 'none') return 'setup';
  if (domain.state === 'verified' || domain.state === 'active' || domain.itMail !== null) return 'status';
  return 'setup';
}

/** „Jetzt prüfen" (`POST …/domain/check`, at most every 10 s). */
export function useDomainCheck() {
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function check() {
    setChecking(true);
    setError(null);
    try {
      const next = await api<Domain>('/settings/organization/domain/check', { method: 'POST', body: {} });
      queryClient.setQueryData(organizationKeys.domain, next);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setChecking(false);
    }
  }
  return { check, checking, error };
}

/** Onboarding Admin 06–08 · Domain: host, availability, records and IT guide, then the waiting/verified card. */
export function DomainStep() {
  const domain = useDomain({ refetchInterval: 30_000 });
  const [view, setView] = useState<View | null>(null);
  if (domain.isPending) {
    return (
      <>
        <DomainIntro />
        <div className={styles.skeletonStack}>
          <Skeleton variant="block" height={230} />
          <Skeleton variant="block" height={360} />
        </div>
      </>
    );
  }
  if (domain.isError) {
    return (
      <>
        <DomainIntro />
        <LoadError
          what="Die Domain konnte nicht geladen werden"
          error={domain.error}
          retrying={domain.isFetching}
          onRetry={() => void domain.refetch()}
        />
      </>
    );
  }
  const current = view ?? initialView(domain.data);
  return current === 'status' ? (
    <DomainStatus domain={domain.data} onBack={() => setView('setup')} />
  ) : (
    <DomainSetup domain={domain.data} onStatus={() => setView('status')} />
  );
}

function DomainIntro() {
  return (
    <WizardIntro
      eyebrow={stepEyebrow('domain')}
      title="Adresse des Mandantenportals"
      textWidth={620}
      text="Das Portal läuft unter einer Subdomain Ihrer Firmendomain. Dafür braucht Ihre IT zwei DNS-Einträge — die Anleitung schicken wir direkt weiter."
    />
  );
}

function DomainSetup({ domain, onStatus }: { domain: Domain; onStatus: () => void }) {
  const nav = useWizardNav();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [subdomain, setSubdomain] = useState(domain.subdomain ?? 'portal');
  const [zone, setZone] = useState(domain.zone ?? '');
  const [available, setAvailable] = useState<DomainAvailability | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    subdomain?: string | undefined;
    domain?: string | undefined;
  }>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState<'check' | 'next' | 'interim' | 'provision' | null>(null);
  const [mailOpen, setMailOpen] = useState(false);
  const reserved = domain.state !== 'none';
  const changed =
    subdomain.trim().toLowerCase() !== (domain.subdomain ?? '') ||
    zone.trim().toLowerCase() !== (domain.zone ?? '');

  function fail(error: unknown) {
    if (error instanceof ApiError && (error.fields['subdomain'] || error.fields['domain'])) {
      setFieldErrors({ subdomain: error.fields['subdomain'], domain: error.fields['domain'] });
    } else {
      setFailure(errorMessage(error));
    }
  }

  async function run<T>(kind: typeof busy, action: () => Promise<T>): Promise<T | null> {
    setBusy(kind);
    setFailure(null);
    setFieldErrors({});
    try {
      return await action();
    } catch (error) {
      fail(error);
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function check() {
    setAvailable(null);
    const result = await run('check', () =>
      api<DomainAvailability>('/settings/organization/domain/availability', {
        method: 'POST',
        body: { subdomain, domain: zone },
      }),
    );
    if (result) setAvailable(result);
  }

  async function next() {
    if (!reserved && subdomain.trim() === '' && zone.trim() === '') {
      await run('next', () => nav.complete('domain', 'skip', 'telephony'));
      return;
    }
    if (!reserved || changed) {
      const next = await run('next', () =>
        api<Domain>('/settings/organization/domain', { method: 'PUT', body: { subdomain, domain: zone } }),
      );
      if (next) {
        queryClient.setQueryData(organizationKeys.domain, next);
        setAvailable(null);
      }
      return;
    }
    onStatus();
  }

  async function interim() {
    const next = await run('interim', () =>
      api<Domain>('/settings/organization/domain/interim', { method: 'POST', body: {} }),
    );
    if (next) {
      queryClient.setQueryData(organizationKeys.domain, next);
      await run('interim', () => nav.complete('domain', 'continue', 'telephony'));
    }
  }

  async function provision() {
    const next = await run('provision', () =>
      api<Domain>('/settings/organization/domain/provision', { method: 'POST', body: {} }),
    );
    if (next) {
      queryClient.setQueryData(organizationKeys.domain, next);
      onStatus();
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(recordsText(domain));
      toast.toast({ message: 'Werte kopiert.' });
    } catch {
      setFailure('Die Werte konnten nicht in die Zwischenablage kopiert werden.');
    }
  }

  const showRecords = reserved && !changed;
  return (
    <>
      <DomainIntro />
      <section className={styles.domainCard} aria-label="Adresse des Mandantenportals">
        <div className={styles.hostRow}>
          <WizardField
            label="Subdomain"
            className={styles.subdomainField}
            value={subdomain}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              setSubdomain(event.target.value);
              setAvailable(null);
            }}
            error={fieldErrors.subdomain}
          />
          <span className={styles.dot} aria-hidden="true">
            .
          </span>
          <WizardField
            label="Ihre Domain"
            className={styles.domainField}
            value={zone}
            autoComplete="off"
            spellCheck={false}
            placeholder="firma.de"
            onChange={(event) => {
              setZone(event.target.value);
              setAvailable(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void check();
              }
            }}
            error={fieldErrors.domain}
          />
          <WizardButton
            variant="secondary"
            className={styles.checkButton}
            loading={busy === 'check'}
            onClick={() => void check()}
          >
            Prüfen
          </WizardButton>
        </div>
        {available ? (
          <div className={styles.bannerSuccess} role="status">
            {available.message}
          </div>
        ) : null}
        {failure ? (
          <div className={styles.bannerDanger} role="alert">
            {failure}
          </div>
        ) : null}
        <div className={styles.bannerNeutral}>
          <span>
            Alternativ ohne eigene Domain starten:{' '}
            <span className={styles.strongText}>{domain.interim.address}</span> · später umstellbar
          </span>
        </div>
      </section>
      {showRecords ? (
        <section className={styles.domainCard} aria-labelledby="domain-it-title">
          <div className={styles.cardTitleRow}>
            <h2 id="domain-it-title" className={styles.cardTitle}>
              Was Ihre IT tun muss
            </h2>
            <span className={styles.cardAside}>zwei Einträge, etwa fünf Minuten</span>
          </div>
          <RecordsTable records={domain.records} />
          <ol className={styles.itSteps}>
            <li className={styles.itStep}>
              <span className={styles.itNumber} aria-hidden="true">
                1
              </span>
              Im DNS-Verwaltungsbereich Ihres Anbieters anmelden (IONOS, Strato, Cloudflare, united-domains).
            </li>
            <li className={styles.itStep}>
              <span className={styles.itNumber} aria-hidden="true">
                2
              </span>
              Beide Einträge für die Zone {domain.zone} anlegen und speichern.
            </li>
            <li className={styles.itStep}>
              <span className={styles.itNumber} aria-hidden="true">
                3
              </span>
              Nichts weiter — DAMOKLES prüft alle 15 Minuten und stellt das TLS-Zertifikat selbst aus.
            </li>
          </ol>
          <div className={styles.buttonRow}>
            {domain.provisioner.available ? (
              <WizardButton
                variant="primary"
                className={styles.cardButton}
                loading={busy === 'provision'}
                onClick={() => void provision()}
              >
                Einträge automatisch setzen
              </WizardButton>
            ) : null}
            <WizardButton
              variant={domain.provisioner.available ? 'secondary' : 'primary'}
              className={styles.cardButton}
              onClick={() => setMailOpen(true)}
            >
              Anleitung an IT senden
            </WizardButton>
            <a
              className={clsx('pressable', domainCardStyles.action, styles.cardButton)}
              href="/api/settings/organization/domain/guide.pdf"
              download
            >
              Als PDF exportieren
            </a>
            <WizardButton
              variant="secondary"
              className={clsx(styles.cardButton, styles.quiet)}
              onClick={() => void copy()}
            >
              Werte kopieren
            </WizardButton>
          </div>
        </section>
      ) : null}
      <WizardFooter
        back={{ label: 'Zurück', onClick: () => nav.go('legal-texts') }}
        secondary={{
          label: 'Übergangsadresse nutzen',
          loading: busy === 'interim',
          onClick: () => void interim(),
        }}
        primary={{ label: 'Weiter', loading: busy === 'next', onClick: () => void next() }}
      />
      <ItMailDialog open={mailOpen} onClose={() => setMailOpen(false)} variant="wizard" onSent={onStatus} />
    </>
  );
}

function DomainStatus({ domain, onBack }: { domain: Domain; onBack: () => void }) {
  const nav = useWizardNav();
  const check = useDomainCheck();
  const [pending, setPending] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  async function next() {
    setPending(true);
    setStepError(null);
    try {
      await nav.complete('domain', 'continue', 'telephony');
    } catch (error) {
      setStepError(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  const done = domain.state === 'verified' || domain.state === 'active';
  return (
    <>
      <div className={styles.statusRow}>
        {done ? (
          <DomainVerifiedCard domain={domain} />
        ) : (
          <DomainWaitingCard
            domain={domain}
            checking={check.checking}
            checkError={check.error}
            onCheck={() => void check.check()}
            primary={
              <WizardButton
                variant="primary"
                className={styles.cardButton}
                loading={pending}
                onClick={() => void next()}
              >
                Weiter zur Telefonie
              </WizardButton>
            }
          />
        )}
      </div>
      {stepError ? (
        <div className={styles.error} role="alert">
          {stepError}
        </div>
      ) : null}
      {done ? (
        <WizardFooter
          back={{ label: 'Zurück', onClick: onBack }}
          primary={{ label: 'Weiter zur Telefonie', loading: pending, onClick: () => void next() }}
        />
      ) : null}
    </>
  );
}
